import {
    GetApplicationResponse,
    PostApplicationRequestBody,
    PostStateRequestBody,
    PutApplicationRequestBody,
    PutStateRequestBody
} from '@iwoca/lapi-client/edge';
import { currencyToNumber } from '@iwoca/orion';
import produce from 'immer';
import { v4 } from 'uuid';

import { TRecursiveNonNullable } from '@lendingAPI/lendingAPI.types';
import { TGetStateResponse } from '@lendingAPI/lendingAPIQueries';

import { TGetStartedData } from './GetStartedUK';

type TPostApplicationRequestBody =
    TRecursiveNonNullable<PostApplicationRequestBody>;

export const serialisePostStateBody = (
    values: TGetStartedData
): PostStateRequestBody => {
    return {
        data: {
            application: {
                people: [
                    {
                        emails: [
                            {
                                email: values.email,
                                marketing_opt_in: {
                                    agreed: values.marketingOptIn,
                                    datetime: new Date().toISOString()
                                },
                                type: 'primary',
                                uid: v4()
                            }
                        ],
                        uid: v4(),
                        roles: ['applicant', 'director', 'guarantor']
                    }
                ]
            }
        }
    };
};

export const serialisePutStateBody = (
    values: TGetStartedData,
    existingStateData: TGetStateResponse['data']
): PutStateRequestBody => {
    const updatedStateData = produce(existingStateData, (newState) => {
        const emails = newState.application?.people?.[0]?.emails?.[0];

        if (emails) {
            emails.marketing_opt_in = {
                agreed: values.marketingOptIn,
                datetime: new Date().toISOString()
            };
        }
    });

    return {
        data: updatedStateData
    };
};

export const getAmount = (amount: string) => {
    return currencyToNumber(amount, {
        currencySymbol: true,
        locale: 'en-GB',
        minorUnits: false
    });
};
const getDuration = (
    duration: TGetStartedData['loanDuration']
): TPostApplicationRequestBody['data']['requested_products'][number]['duration'] => {
    switch (duration) {
        case '60-months':
            return {
                amount: 60,
                unit: 'months'
            };
        case '48-months':
            return {
                amount: 48,
                unit: 'months'
            };
        case '36-months':
            return {
                amount: 36,
                unit: 'months'
            };
        case '24-months':
            return {
                amount: 24,
                unit: 'months'
            };
        case '12-months':
            return {
                amount: 12,
                unit: 'months'
            };
    }
};

export type TPrefilledStateData = {
    email?: string;
    marketingOptIn?: boolean;
};

export type TPrefilledApplicationData = {
    amount?: string;
    loanDuration?: TGetStartedData['loanDuration'];
    loanPurpose?: TGetStartedData['loanPurpose'];
};

export const normaliseState = (
    state?: TGetStateResponse
): TPrefilledStateData => {
    if (!state) return {};

    const customerState = state.data.application?.people?.[0];

    return {
        email: customerState?.emails?.[0].email || '',
        marketingOptIn:
            customerState?.emails?.[0].marketing_opt_in?.agreed || false
    };
};

export const normaliseApplication = (
    application?: GetApplicationResponse
): TPrefilledApplicationData => {
    if (!application) return {};

    const customerApplication = application.data.requested_products?.[0];
    const formatAmount = customerApplication?.amount
        ? new Intl.NumberFormat('en-gb').format(customerApplication?.amount)
        : '';
    const normaliseDuration =
        `${customerApplication?.duration.amount}-${customerApplication?.duration.unit}` as TGetStartedData['loanDuration'];

    return {
        amount: formatAmount,
        loanDuration:
            customerApplication && customerApplication.duration
                ? normaliseDuration
                : undefined,
        loanPurpose:
            (customerApplication?.purpose as TGetStartedData['loanPurpose']) ||
            undefined
    };
};

export const serialiseApplicationBody = (
    values: TGetStartedData
): PostApplicationRequestBody | PutApplicationRequestBody => {
    return {
        data: {
            requested_products: [
                {
                    amount: getAmount(values.amount),
                    duration: getDuration(values.loanDuration),
                    product_type: 'credit_facility',
                    purpose: values.loanPurpose
                }
            ]
        }
    };
};
